import React, { useEffect, useRef, useState } from "react";
import Spinner from "../../../components/Spinner/Spinner.tsx";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";
import { generatePDFDocumentFromRef } from "../../../util/generatePDF.tsx";
import { submitContact } from "../../../api/contact";

const PROPERTY_TYPE_LABELS: Record<string, string> = {
  flat: "Flat",
  terraced_house: "Terraced house",
  "semi-detached_house": "Semi-detached house",
  detached_house: "Detached house",
};

// Removed bathrooms from interface
interface RentEstimateInputs {
  postcode: string;
  bedrooms: number;
  type: string;
}

interface RentEstimateResult {
  status: "null" | "success" | "error";
  data?: number[];
  message?: string;
}

interface RentEstimatePage2Props {}

export default function RentEstimatePage2(
  props: RentEstimatePage2Props
): JSX.Element {
  const location = useLocation();
  const [valuationName, setValuationName] = useState("");
  const [valuationEmail, setValuationEmail] = useState("");
  const [valuationSubmitting, setValuationSubmitting] = useState(false);
  const [valuationError, setValuationError] = useState<string | null>(null);
  const [valuationSuccess, setValuationSuccess] = useState(false);

  const [inputs, setInputs] = useState<RentEstimateInputs>({
    postcode: "W1J 8DZ",
    bedrooms: 2,
    type: "flat",
  });

  const [result, setResult] = useState<RentEstimateResult>({ status: "null" });
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState<boolean>(false);
  const tableRef = useRef<HTMLDivElement>(null);
  const [valuationNotes, setValuationNotes] = useState<string>("");

  const handleValuationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValuationError(null);
    setValuationSuccess(false);
    setValuationSubmitting(true);

    try {
      await submitContact({
        name: valuationName.trim(),
        email: valuationEmail.trim(),
        subject: "In-Person Rent Valuation",
        message: valuationNotes.trim() || "Request for in-person rent valuation",
        sourcePage: location.pathname,
        enquiryType: "valuation",
      });

      setValuationSuccess(true);
      setValuationName("");
      setValuationEmail("");
      setValuationNotes("");
    } catch (err) {
      setValuationError(
        err instanceof Error ? err.message : "Failed to send your enquiry"
      );
    } finally {
      setValuationSubmitting(false);
    }
  };

  async function handleEstimateRent(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    if (!inputs.postcode.replaceAll(" ", "")) return;
    try {
      const response15 = await fetch(
        `https://api.propertydata.co.uk/rents?key=0YU2HWR2QR&postcode=${inputs.postcode.replaceAll(
          " ",
          ""
        )}&bedrooms=${inputs.bedrooms}&type=${inputs.type}&points=15`
      );
      const response100 = await fetch(
        `https://api.propertydata.co.uk/rents?key=0YU2HWR2QR&postcode=${inputs.postcode.replaceAll(
          " ",
          ""
        )}&bedrooms=${inputs.bedrooms}&type=${inputs.type}&points=100`
      );
      const result = [await response15.json(), await response100.json()];
      if (result[0].status === "success" && result[1].status === "success") {
        const data = [
          result[0].data.long_let.average,
          result[1].data.long_let.average,
        ];
        setResult({
          status: "success",
          data: [Math.min(...data), Math.max(...data)],
        });
      } else if (result[0].status === "error") {
        setResult({ status: "error", message: result[0].message });
        setError(result[0].message);
      } else if (result[1].status === "error") {
        setResult({ status: "error", message: result[1].message });
        setError(result[1].message);
      }
    } catch (error: any) {
      console.error("Error fetching rental estimate:", error);
      setError(
        "There was an error fetching rental estimate, please try again later."
      );
    } finally {
      setLoading(false);
    }
  }

  function getPropertyTypeLabel(input: string): string {
    const known = PROPERTY_TYPE_LABELS[input];
    if (known) return known;
    // Fallback: turn "semi-detached_house" -> "Semi detached house"
    const normalized = input.replace(/[_-]+/g, " ").trim();
    return normalized.replace(/\b\w/g, (c) => c.toUpperCase());
  }

  function handleDownloadPDF() {
    setIsGeneratingPDF(true);
  }

  useEffect(() => {
    (async () => {
      if (isGeneratingPDF && tableRef.current) {
        try {
          const doc = await generatePDFDocumentFromRef(tableRef);
          if (doc) {
            doc.save(`Rental Income Estimate.pdf`);
            setIsGeneratingPDF(false);
          }
        } catch (error: any) {
          console.error(error);
        } finally {
          setIsGeneratingPDF(false);
        }
      }
    })();
  }, [isGeneratingPDF, tableRef]);

  function formatMonthlyPrice(price: number): string {
    return ((price * 52) / 12).toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }

  // Prefill valuation notes with inputs for convenience
  useEffect(() => {
    const parts = [
      inputs.postcode ? `Location: ${inputs.postcode}` : null,
      inputs.type ? `Asset Type: ${getPropertyTypeLabel(inputs.type)}` : null,
      Number.isFinite(inputs.bedrooms) ? `Bedrooms: ${inputs.bedrooms}` : null,
    ].filter(Boolean);
    const autoNote = parts.join(" | ");
    setValuationNotes((prev) => {
      // If user has typed something custom, don't overwrite their input
      if (prev && prev !== "" && !prev.startsWith("Location:")) {
        return prev;
      }
      return autoNote;
    });
  }, [inputs.postcode, inputs.type, inputs.bedrooms]);

  // Helper to return new styles when type is selected
  function assetTypeButtonClass(selected: boolean) {
    return [
      "flex",
      "flex-col",
      "items-center",
      "justify-center",
      "h-12",
      "border",
      "border-solid",
      "transition-all",
      "duration-300",
      "transform",
      "lg:flex-row",
      "lg:h-16",
      "lg:px-8",
      "lg:gap-4",
      "lg:justify-start",
      selected
        ? "border-brand-yellow bg-bg-porcelain text-text-obsidian"
        : "bg-bg-porcelain border-[#ddd]",
    ]
      .filter(Boolean)
      .join(" ");
  }

  return (
    <>
      <Helmet>
        <title>Rent Estimate | Letting Engine</title>
        <meta
          name="description"
          content="Unlock the true rental potential of your asset. Our algorithm analyzes thousands of real-time data points to provide an instant, market-leading estimate."
        />
        <meta
          name="keywords"
          content="residential property letting agency, real estate, london, rent estimate, rent, property valuation"
        />
      </Helmet>

      {/* HEADER */}
      <section className="page-header py-32 pb-16 max-w-[1600px] mx-auto px-[5%]">
        <h1 className="font-teko text-7xl tracking-[-1px] mb-4">
          Rental Income Estimate
        </h1>
        <p className="font-manrope text-lg text-text-slate max-w-[700px] leading-relaxed">
          Unlock the true rental potential of your asset. Our algorithm analyzes
          thousands of real-time data points to provide an instant,
          market-leading estimate.
        </p>
      </section>

      {/* MAIN LAYOUT */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] lg:gap-10 max-w-[1600px] mx-auto px-[5%] pb-32 ">
        {/* LEFT: THE TOOL */}
        <div className="">
          <div
            className="bg-bg-white border border-solid border-[rgba(0,0,0,0.05)] p-6 sm:p-16 relative overflow-hidden clip-path-[polygon(0_0,100%_0,100%_95%,95%_100%,0_100%)] shadow-soft"
            ref={tableRef}
          >
            <div className="border-b border-b-solid border-b-[rgba(0,0,0,0.05)] pb-8 mb-12">
              <div className="flex justify-between items-center">
                <h3 className="font-teko text-3xl text-text-obsidian">
                  Property Details
                </h3>
                <div className="inline-flex items-center gap-2 font-manrope text-xs uppercase tracking-wider text-[#15803D] bg-[#F0FDF4] py-1.5 px-3 border border-solid border-[#DCFCE7] transform">
                  <div className="w-2 h-2 bg-[#15803D] rounded-full animate-pulse"></div>{" "}
                  Live Data Feed
                </div>
              </div>
            </div>

            <form className="grid gap-10" onSubmit={handleEstimateRent}>
              {/* Step 1: Location */}
              <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3">
                <label
                  htmlFor="postcode"
                  className="block font-teko text-lg mb-3 uppercase text-text-obsidian tracking-wide w-40 min-w-[10rem]"
                >
                  Property Location
                </label>
                <input
                  type="text"
                  className="flex-1 p-4 bg-bg-porcelain border border-solid border-[#ddd] font-manrope text-lg transition-all duration-300 clip-path-[polygon(0_0,100%_0,100%_85%,98%_100%,0_100%)] focus:outline-none focus:border-brand-yellow focus:bg-white"
                  placeholder="Enter Postcode or Street Name"
                  name="postcode"
                  value={inputs.postcode}
                  onChange={(e) =>
                    setInputs({ ...inputs, postcode: e.target.value })
                  }
                  id="postcode"
                />
              </div>
              {/* Step 2: Type */}
              <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3">
                <label className="block font-teko text-lg mb-3 uppercase text-text-obsidian tracking-wide w-40 min-w-[10rem]">
                  Asset Type
                </label>
                <div className="flex gap-4 flex-wrap flex-col sm:flex-row flex-1 w-full">
                  {/* Flat */}
                  <label
                    className="flex-1 cursor-pointer relative min-w-[120px]"
                    tabIndex={0}
                  >
                    <input
                      type="radio"
                      name="type"
                      className="sr-only"
                      value="flat"
                      checked={inputs.type === "flat"}
                      onChange={(e) =>
                        setInputs({ ...inputs, type: e.target.value })
                      }
                      tabIndex={-1}
                    />
                    <div
                      className={assetTypeButtonClass(inputs.type === "flat")}
                    >
                      <div className="transform text-center lg:text-left">
                        <span className="font-teko text-lg uppercase bg-transparent">
                          Apartment
                        </span>
                      </div>
                      {inputs.type === "flat" && (
                        <span
                          className="absolute top-2 right-2 text-brand-yellow text-base"
                          aria-label="Selected"
                        >
                          ✓
                        </span>
                      )}
                    </div>
                  </label>
                  {/* Terraced House */}
                  <label
                    className="flex-1 cursor-pointer relative min-w-[120px]"
                    tabIndex={0}
                  >
                    <input
                      type="radio"
                      name="type"
                      className="sr-only"
                      value="terraced_house"
                      checked={inputs.type === "terraced_house"}
                      onChange={(e) =>
                        setInputs({ ...inputs, type: e.target.value })
                      }
                      tabIndex={-1}
                    />
                    <div
                      className={assetTypeButtonClass(
                        inputs.type === "terraced_house"
                      )}
                    >
                      <div className="transform text-center lg:text-left">
                        <span className="font-teko text-lg uppercase bg-transparent">
                          House
                        </span>
                      </div>
                      {inputs.type === "terraced_house" && (
                        <span
                          className="absolute top-2 right-2 text-brand-yellow text-base"
                          aria-label="Selected"
                        >
                          ✓
                        </span>
                      )}
                    </div>
                  </label>
                  {/* Semi-detached House */}
                  <label
                    className="flex-1 cursor-pointer relative min-w-[120px]"
                    tabIndex={0}
                  >
                    <input
                      type="radio"
                      name="type"
                      className="sr-only"
                      value="semi-detached_house"
                      checked={inputs.type === "semi-detached_house"}
                      onChange={(e) =>
                        setInputs({ ...inputs, type: e.target.value })
                      }
                      tabIndex={-1}
                    />
                    <div
                      className={assetTypeButtonClass(
                        inputs.type === "semi-detached_house"
                      )}
                    >
                      <div className="transform text-center lg:text-left">
                        <span className="font-teko text-lg uppercase bg-transparent">
                          Semi-Detached
                        </span>
                      </div>
                      {inputs.type === "semi-detached_house" && (
                        <span
                          className="absolute top-2 right-2 text-brand-yellow text-base"
                          aria-label="Selected"
                        >
                          ✓
                        </span>
                      )}
                    </div>
                  </label>
                </div>
              </div>
              {/* Step 3: Configuration */}
              <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3">
                <label className="block font-teko text-lg mb-3 uppercase text-text-obsidian tracking-wide w-40 min-w-[10rem]">
                  Bedrooms
                </label>
                <div className="flex border border-solid border-[#ddd] w-fit transform">
                  <button
                    type="button"
                    className="py-4 px-6 bg-white border-none cursor-pointer text-lg transition-all duration-200 hover:bg-brand-yellow"
                    onClick={() =>
                      setInputs({
                        ...inputs,
                        bedrooms: Math.max(0, inputs.bedrooms - 1),
                      })
                    }
                  >
                    -
                  </button>
                  <div className="py-4 px-8 font-teko text-xl border-l border-l-solid border-l-[#ddd] border-r border-r-solid border-r-[#ddd] bg-bg-porcelain">
                    {inputs.bedrooms}
                  </div>
                  <button
                    type="button"
                    className="py-4 px-6 bg-white border-none cursor-pointer text-lg transition-all duration-200 hover:bg-brand-yellow"
                    onClick={() =>
                      setInputs({ ...inputs, bedrooms: inputs.bedrooms + 1 })
                    }
                  >
                    +
                  </button>
                </div>
              </div>
              <button
                type="submit"
                className="w-full p-6 bg-brand-yellow mt-8 border-none text-text-obsidian font-teko text-2xl uppercase tracking-wide cursor-pointer transition-all duration-300 clip-path-[polygon(0_0,100%_0,100%_85%,97%_100%,0_100%)] hover:translate-y-[-3px] hover:shadow-[0_15px_30px_rgba(255,196,0,0.2)]"
              >
                {loading ? <Spinner /> : "Generate Analysis"}
              </button>
            </form>

            {result.status === "success" && result.data && (
              <div className="mt-14 border-t border-t-solid border-t-[rgba(0,0,0,0.05)] pt-12">
                <div className="bg-[linear-gradient(120deg,#ffffff_0%,#fff8e6_100%)] border border-solid border-[rgba(0,0,0,0.06)] p-8 sm:p-10 shadow-soft clip-path-[polygon(0_0,100%_0,100%_92%,96%_100%,0_100%)]">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <span className="font-manrope text-xs uppercase tracking-wider text-text-slate block">
                        Estimated rent range
                      </span>
                      <h3 className="font-teko text-5xl sm:text-6xl text-text-obsidian leading-none mt-2">
                        £{formatMonthlyPrice(result.data[0])}–£
                        {formatMonthlyPrice(result.data[1])}
                        <span className="text-brand-yellow"> / month</span>
                      </h3>
                      <div className="font-manrope text-sm text-text-slate mt-2">
                        £{result.data[0].toLocaleString()}–£
                        {result.data[1].toLocaleString()} / week (source range)
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2 sm:mt-0">
                      <span className="text-xs font-manrope uppercase tracking-wider bg-white px-3 py-1 border border-solid border-[rgba(0,0,0,0.08)]">
                        {getPropertyTypeLabel(inputs.type)}
                      </span>
                      <span className="text-xs font-manrope uppercase tracking-wider bg-white px-3 py-1 border border-solid border-[rgba(0,0,0,0.08)]">
                        {inputs.bedrooms} bed
                      </span>
                      <span className="text-xs font-manrope uppercase tracking-wider bg-white px-3 py-1 border border-solid border-[rgba(0,0,0,0.08)]">
                        {inputs.postcode}
                      </span>
                    </div>
                  </div>

                  <p className="font-manrope text-base text-text-slate leading-relaxed mt-6">
                    Based on comparable long-let data, a{" "}
                    <span className="font-semibold text-text-obsidian">
                      {getPropertyTypeLabel(inputs.type)}
                    </span>{" "}
                    in{" "}
                    <span className="font-semibold text-text-obsidian">
                      {inputs.postcode}
                    </span>{" "}
                    with{" "}
                    <span className="font-semibold text-text-obsidian">
                      {inputs.bedrooms} bedroom{inputs.bedrooms > 1 ? "s" : ""}
                    </span>{" "}
                    is estimated at the range above.
                  </p>

                  <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3">
                    <button
                      className="w-full sm:w-auto px-8 py-3 bg-text-obsidian text-white font-teko text-xl uppercase tracking-wide cursor-pointer transition-all duration-300 clip-path-[polygon(0_0,100%_0,100%_85%,97%_100%,0_100%)] hover:bg-brand-yellow hover:text-text-obsidian"
                      onClick={handleDownloadPDF}
                      type="button"
                    >
                      {isGeneratingPDF ? (
                        <>
                          <Spinner /> Downloading PDF...
                        </>
                      ) : (
                        <>Download PDF</>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {error && <p className="text-red-500 mt-4 text-center">{error}</p>}
          </div>
        </div>

        {/* RIGHT: SIDEBAR */}
        <div className="relative w-full lg:w-[400px] mt-20 lg:mt-0">
          <div className="sticky top-32">
            <div className="bg-[var(--glass-surface)] backdrop-blur-[25px] border-[var(--glass-border)] p-6 md:p-12 shadow-[0_30px_60px_rgba(0,0,0,0.05)] clip-path-[polygon(10%_0,100%_0,100%_100%,0%_100%,0_5%)]">
              <h3 className="font-teko text-3xl text-text-obsidian mb-2">
                Professional Valuation
              </h3>
              <p className="font-manrope text-sm text-text-slate leading-relaxed mb-6">
                While our instant tool provides a solid baseline, a physical
                inspection allows us to factor in finish quality, view, and
                specific street appeal.
              </p>

              <form className="grid gap-4" onSubmit={handleValuationSubmit}>
                {valuationSuccess && (
                  <p className="font-manrope text-sm text-green-700 bg-green-50 p-4 border border-solid border-green-200">
                    Thank you — your valuation request has been sent.
                  </p>
                )}
                {valuationError && (
                  <p className="font-manrope text-sm text-red-700 bg-red-50 p-4 border border-solid border-red-200">
                    {valuationError}
                  </p>
                )}
                <div>
                  <label
                    htmlFor="valuationName"
                    className="font-manrope text-xs uppercase tracking-[.15em] text-text-slate block mb-1"
                  >
                    Name
                  </label>
                  <input
                    id="valuationName"
                    type="text"
                    className="w-full p-3 bg-white border border-solid border-[rgba(0,0,0,0.1)] font-manrope text-base text-text-obsidian focus:outline-none focus:border-brand-yellow transition-all duration-200"
                    placeholder="Full Name"
                    value={valuationName}
                    onChange={(e) => setValuationName(e.target.value)}
                    required
                    disabled={valuationSubmitting}
                  />
                </div>
                <div>
                  <label
                    htmlFor="valuationEmail"
                    className="font-manrope text-xs uppercase tracking-[.15em] text-text-slate block mb-1"
                  >
                    Email
                  </label>
                  <input
                    id="valuationEmail"
                    type="email"
                    className="w-full p-3 bg-white border border-solid border-[rgba(0,0,0,0.1)] font-manrope text-base text-text-obsidian focus:outline-none focus:border-brand-yellow transition-all duration-200"
                    placeholder="you@example.com"
                    value={valuationEmail}
                    onChange={(e) => setValuationEmail(e.target.value)}
                    required
                    disabled={valuationSubmitting}
                  />
                </div>
                <div>
                  <label
                    htmlFor="valuationNotes"
                    className="font-manrope text-xs uppercase tracking-[.15em] text-text-slate block mb-1"
                  >
                    Notes
                  </label>
                  <textarea
                    id="valuationNotes"
                    rows={3}
                    className="w-full p-3 bg-white border border-solid border-[rgba(0,0,0,0.1)] font-manrope text-base text-text-obsidian focus:outline-none focus:border-brand-yellow transition-all duration-200"
                    placeholder="Tell us about your property..."
                    value={valuationNotes}
                    onChange={(e) => setValuationNotes(e.target.value)}
                    disabled={valuationSubmitting}
                  ></textarea>
                </div>
                <button
                  type="submit"
                  className="w-full p-4 border border-solid border-text-obsidian bg-transparent text-text-obsidian font-teko text-xl uppercase cursor-pointer transition-all duration-300 hover:bg-text-obsidian hover:text-white disabled:opacity-60 disabled:cursor-not-allowed"
                  disabled={valuationSubmitting}
                >
                  {valuationSubmitting
                    ? "Sending…"
                    : "Request In-Person Valuation"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* SEO CONTENT */}
      <section className="max-w-[1440px] mx-auto px-[5%] py-32 border-t border-t-solid border-t-[rgba(0,0,0,0.05)] sm:mt-16">
        <h3 className="font-teko text-3xl mb-6 border-l-4 border-l-solid border-l-brand-yellow pl-4">
          Precision Pricing
        </h3>
        <p className="font-manrope text-base leading-relaxed text-text-slate mb-6 max-w-[800px]">
          In the London luxury market, pricing is a science. Overpricing leads
          to stagnant listings and "digital decay," while underpricing leaves
          equity on the table. Our engine uses algorithmic triangulation to find
          the sweet spot.
        </p>

        <h3 className="font-teko text-3xl mb-6 border-l-4 border-l-solid border-l-brand-yellow pl-4">
          Yield Maximization
        </h3>
        <p className="font-manrope text-base leading-relaxed text-text-slate mb-6 max-w-[800px]">
          Beyond simple rent, we analyze your asset for potential capital
          improvements. A strategic refurbishment or furnishing upgrade can
          often unlock 15-20% higher yields. Our valuation report includes these
          "Value Add" suggestions.
        </p>
      </section>
    </>
  );
}
